import StatusPill, { type Tone } from '@/components/console/status-pill';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardFooter, CardHeader } from '@/components/ui/card';
import { formatDateTime, humanize } from '@/lib/format';
import { app_draft_acceptance_path, app_draft_rejection_path, billing_path } from '@/rails/routes';
import type { AppList, AppShell, Draft, DraftChanges, FieldType } from '@/types/platform';
import { Link, router } from '@inertiajs/react';
import { useState } from 'react';

interface DraftCardProps {
  app: AppShell;
  draft: Draft;
}

interface DraftState {
  label: string;
  tone: Tone;
}

const FIELD_TYPE_LABELS: Record<FieldType, string> = {
  text: 'Short text',
  long_text: 'Long text',
  number: 'Number',
  money: 'Money',
  date: 'Date',
  choice: 'Choice',
  checkbox: 'Yes / No',
  email: 'Email',
  phone: 'Phone',
  customer: 'Customer',
  attachments: 'Photos and files',
  location: 'Map location',
};

function draftState(draft: Draft): DraftState {
  if (draft.generating) return { label: 'Drafting…', tone: 'neutral' };
  if (draft.ready) return { label: 'Ready to review', tone: 'info' };
  if (draft.status === 'accepted') return { label: 'Accepted', tone: 'success' };
  if (draft.status === 'rejected') return { label: 'Set aside', tone: 'neutral' };
  if (draft.status === 'failed') return { label: 'Could not be used', tone: 'danger' };

  return { label: 'Waiting', tone: 'neutral' };
}

const DraftCard = ({ app, draft }: DraftCardProps) => {
  const [busy, setBusy] = useState(false);
  const state = draftState(draft);
  const visitOptions = { onStart: () => setBusy(true), onFinish: () => setBusy(false) };

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-center gap-2">
          <StatusPill tone={state.tone}>{state.label}</StatusPill>
          <span className="text-muted-foreground text-sm">
            {draft.requestedBy ? `${draft.requestedBy} · ` : ''}
            {formatDateTime(draft.createdAt)}
          </span>
        </div>
        <p className="text-sm break-words whitespace-pre-wrap">{draft.prompt}</p>
      </CardHeader>

      {draft.generating ? (
        <CardContent>
          <p className="text-muted-foreground text-sm">This usually takes a few seconds.</p>
        </CardContent>
      ) : null}

      {draft.ready ? (
        <>
          <CardContent className="space-y-4">
            {draft.changes ? (
              <ChangeSummary changes={draft.changes} proposal={draft.proposal} app={app} />
            ) : null}
            <Proposal lists={draft.proposal} />
          </CardContent>
          <CardFooter className="flex-wrap gap-2">
            <Button
              size="sm"
              disabled={busy}
              onClick={() =>
                router.post(app_draft_acceptance_path(app.id, draft.id), {}, visitOptions)
              }
            >
              Accept
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={busy}
              onClick={() =>
                router.post(app_draft_rejection_path(app.id, draft.id), {}, visitOptions)
              }
            >
              Set aside
            </Button>
          </CardFooter>
        </>
      ) : null}

      {draft.status === 'failed' ? (
        <CardContent className="space-y-3 text-sm">
          {draft.problems.length > 0 ? (
            <ul className="list-inside list-disc space-y-0.5 text-red-700">
              {draft.problems.map((problem, index) => (
                <li key={index} className="break-words">
                  {problem}
                </li>
              ))}
            </ul>
          ) : null}
          {draft.resolution === 'no_credits' ? (
            <p>
              Build credits ran out for this month, so this was not drafted.{' '}
              <Link href={billing_path()} className="font-medium underline underline-offset-4">
                See plan and usage
              </Link>
            </p>
          ) : (
            <p>
              This went to CAP Rock Labs for review. You can also try describing it another way.
            </p>
          )}
        </CardContent>
      ) : null}
    </Card>
  );
};

interface ChangeSummaryProps {
  changes: DraftChanges;
  proposal: AppList[];
  app: AppShell;
}

// The draft names lists and fields by key. Show the names a person gave them where we know them.
const ChangeSummary = ({ changes, proposal, app }: ChangeSummaryProps) => {
  const proposedListName = (key: string) =>
    proposal.find((list) => list.key === key)?.name ?? humanize(key);
  const currentListName = (key: string) =>
    app.lists.find((list) => list.key === key)?.name ?? proposedListName(key);
  const proposedFieldLabel = (listKey: string, fieldKey: string) =>
    proposal.find((list) => list.key === listKey)?.fields.find((field) => field.key === fieldKey)
      ?.label ?? humanize(fieldKey);

  const unchanged =
    changes.addedLists.length === 0 &&
    changes.removedLists.length === 0 &&
    changes.addedFields.length === 0 &&
    changes.removedFields.length === 0;

  return (
    <div className="space-y-2 text-sm">
      <h3 className="font-medium">What changes</h3>

      {unchanged ? (
        <p className="text-muted-foreground">
          No list or field is added or removed. Names, types, or choices may differ.
        </p>
      ) : null}

      {changes.addedLists.length > 0 ? (
        <p>
          <span className="text-muted-foreground">Lists added: </span>
          {changes.addedLists.map(proposedListName).join(', ')}
        </p>
      ) : null}

      {changes.addedFields.map((entry) => (
        <p key={`added-${entry.list}`}>
          <span className="text-muted-foreground">
            Fields added to {proposedListName(entry.list)}:{' '}
          </span>
          {entry.fields.map((field) => proposedFieldLabel(entry.list, field)).join(', ')}
        </p>
      ))}

      {changes.removedLists.length > 0 ? (
        <div className="rounded-md border border-yellow-200 bg-yellow-50 px-3 py-2 text-yellow-900">
          <p>
            <span className="font-medium">Lists removed: </span>
            {changes.removedLists.map(currentListName).join(', ')}
          </p>
          <p>Records in a removed list are kept but hidden.</p>
        </div>
      ) : null}

      {changes.removedFields.length > 0 ? (
        <div className="rounded-md border border-yellow-200 bg-yellow-50 px-3 py-2 text-yellow-900">
          {changes.removedFields.map((entry) => (
            <p key={`removed-${entry.list}`}>
              <span className="font-medium">
                Fields removed from {currentListName(entry.list)}:{' '}
              </span>
              {entry.fields.map(humanize).join(', ')}
            </p>
          ))}
          <p>What was saved in a removed field is kept but hidden.</p>
        </div>
      ) : null}
    </div>
  );
};

const Proposal = ({ lists }: { lists: AppList[] }) => {
  return (
    <div className="space-y-2 text-sm">
      <h3 className="font-medium">Proposed lists</h3>
      <div className="grid gap-3 sm:grid-cols-2">
        {lists.map((list) => (
          <div key={list.key} className="rounded-md border p-3">
            <h4 className="font-medium">{list.name}</h4>
            <ul className="mt-1.5 space-y-1">
              {list.fields.map((field) => (
                <li key={field.key} className="flex items-baseline justify-between gap-3">
                  <span className="min-w-0 break-words">
                    {field.label}
                    {field.required ? (
                      <span className="text-muted-foreground"> (required)</span>
                    ) : null}
                  </span>
                  <span className="text-muted-foreground shrink-0 text-xs">
                    {FIELD_TYPE_LABELS[field.type] ?? humanize(field.type)}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
};

export default DraftCard;
